import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  Building2, Users, TrendingUp, Shield, FileText, Download, 
  Calendar, ChevronDown, Mail, Phone, ArrowRight
} from "lucide-react";
import { isRTL } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumb from "@/components/Breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function InvestorRelationsPage() {
  const { t, i18n } = useTranslation();
  const rtl = isRTL(i18n.language);
  const [activeYear, setActiveYear] = useState("2024");

  const overviewStats = t("investor_relations.overview_stats", { returnObjects: true }) || [];
  const leadership = t("investor_relations.leadership", { returnObjects: true }) || [];
  const documents = t("investor_relations.documents", { returnObjects: true }) || [];
  const pressReleases = t("investor_relations.press_releases", { returnObjects: true }) || [];
  const governance = t("investor_relations.governance", { returnObjects: true }) || [];
  const faqs = t("investor_relations.faqs", { returnObjects: true }) || [];
  const financialData = t("investor_relations.financial_data", { returnObjects: true }) || {};
  
  const chartData = financialData[activeYear]?.chart_data || [];

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet>
        <title>{t("investor_relations.seo_title")}</title>
        <meta name="description" content={t("investor_relations.seo_desc")} />
      </Helmet>
      <Header />
      <PageHeader 
        title={t("investor_relations.hero_title")} 
        highlight={t("investor_relations.hero_highlight")}
        subtitle={t("investor_relations.hero_subtitle")}
      >
        <Breadcrumb items={[{ label: t("breadcrumb.investor_relations") }]} />
      </PageHeader>

      {/* Company Overview */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white" data-testid="company-overview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.overview_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.overview_title")} <span className="text-[#D4AF37] italic">{t("investor_relations.overview_highlight")}</span>
            </h2>
            <p className="text-[#4A5568] text-base leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("investor_relations.overview_desc")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {overviewStats.map((stat, i) => {
              const icons = [Building2, Users, TrendingUp, Shield];
              const Icon = icons[i % icons.length];
              return (
                <Card key={i} className="bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                  <CardContent className="p-6 text-center">
                    <Icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
                    <div className="text-3xl font-bold text-[#0A192F] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {stat.label}
                    </div>
                    <p className="text-xs text-[#4A5568] mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {stat.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Financial Performance */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="financial-performance">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.financial_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.financial_title")} <span className="text-[#D4AF37] italic">{t("investor_relations.financial_highlight")}</span>
            </h2>
          </div>
          
          <Tabs value={activeYear} onValueChange={setActiveYear} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-8">
              <TabsTrigger value="2024">2024</TabsTrigger>
              <TabsTrigger value="2025">2025</TabsTrigger>
              <TabsTrigger value="2026">2026</TabsTrigger>
            </TabsList>
            
            {["2024", "2025", "2026"].map((year) => (
              <TabsContent key={year} value={year}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Card className="bg-white rounded-sm border border-[#E2E8F0]">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {t("investor_relations.financial_table_title")}
                      </h3>
                      <div className="space-y-4">
                        {(financialData[year]?.metrics || []).map((metric, i) => (
                          <div key={i} className="flex justify-between items-center pb-3 border-b border-[#E2E8F0] last:border-0">
                            <span className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{metric.label}</span>
                            <span className="text-sm font-semibold text-[#0A192F]" style={{ fontFamily: "'Outfit', sans-serif" }}>{metric.value}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white rounded-sm border border-[#E2E8F0]">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {t("investor_relations.financial_chart_title")}
                      </h3>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4A5568" }} />
                          <YAxis tick={{ fontSize: 12, fill: "#4A5568" }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: "#fff", 
                              border: "1px solid #E2E8F0",
                              borderRadius: "4px",
                              fontSize: "12px"
                            }}
                          />
                          <Bar dataKey="value" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white" data-testid="leadership">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.leadership_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.leadership_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {leadership.map((member, i) => (
              <Card key={i} className="bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#0A192F] text-[#D4AF37] flex items-center justify-center text-2xl font-bold mx-auto mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {member.initials}
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {member.name}
                  </h3>
                  <p className="text-sm text-[#D4AF37] font-medium mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {member.title}
                  </p>
                  <p className="text-xs text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reports & Documents */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="documents">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.documents_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.documents_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {documents.map((doc, i) => (
              <Card key={i} className="bg-white rounded-sm border border-[#E2E8F0] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center shrink-0">
                      <FileText className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-[#0A192F] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {doc.title}
                      </h3>
                      <div className="flex items-center gap-4 text-xs text-[#4A5568] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {doc.date}
                        </span>
                        <span>{doc.size}</span>
                      </div>
                      <a 
                        href="#" 
                        className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#B5952F] font-medium transition-colors"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        <Download className="w-4 h-4" />
                        {t("investor_relations.download_btn")}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white" data-testid="press-releases">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.press_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.press_title")}
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className={`absolute ${rtl ? "right-0" : "left-0"} top-0 bottom-0 w-px bg-[#E2E8F0]`} />
              <div className="space-y-8">
                {pressReleases.map((release, i) => (
                  <div key={i} className={`relative ${rtl ? "pr-12" : "pl-12"}`}>
                    <div className={`absolute ${rtl ? "right-0" : "left-0"} top-0 w-3 h-3 rounded-full bg-[#D4AF37] ${rtl ? "-mr-1.5" : "-ml-1.5"}`} />
                    <Card className="bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                      <CardContent className="p-6">
                        <div className="text-xs text-[#D4AF37] font-medium mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {release.date}
                        </div>
                        <h3 className="text-lg font-semibold text-[#0A192F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {release.title}
                        </h3>
                        <p className="text-sm text-[#4A5568] leading-relaxed mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {release.excerpt}
                        </p>
                        <a 
                          href="#" 
                          className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#B5952F] font-medium transition-colors"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          {t("investor_relations.read_more")}
                          <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
                        </a>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Governance */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="governance">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.governance_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.governance_title")}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {governance.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-sm border border-[#E2E8F0]">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#F8F9FA]">
                  <span className="text-base font-semibold text-[#0A192F] text-left" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-sm text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {item.content}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white" data-testid="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("investor_relations.faq_pretitle")}
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("investor_relations.faq_title")}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-white">
                  <span className="text-sm font-medium text-[#0A192F] text-left" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {faq.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-sm text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {faq.a}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-gradient-to-br from-[#0A192F] to-[#112240]" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("investor_relations.contact_title")}
          </h2>
          <p className="text-white/80 text-base leading-relaxed mb-8 max-w-2xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("investor_relations.contact_desc")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-3 text-white">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>investor@olyinvestment.com</span>
            </div>
            <div className="flex items-center justify-center gap-3 text-white">
              <Phone className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>+90 533 800 00 00</span>
            </div>
          </div>
          <Link
            to={`/${i18n.language}/contact`}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] text-white font-medium rounded-sm hover:bg-[#B5952F] transition-colors"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t("investor_relations.contact_btn")}
            <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
